import { useState, useEffect, useMemo } from 'react';
import SystemLogListItem from './components/SystemLogListItem';
import Pagination from '../../components/Pagination/Pagination';
import './SystemLog.css';
import { useSystemLogList } from '../../hooks/queries/logQueries';
import RefreshIcon from '../../assets/icons/refresh.svg?react';
import { useSearchParams } from 'react-router-dom';
import { SkeletonTable } from '../../components/Skeleton';
import Calendar from '../../components/Calendar';
import dayjs from 'dayjs';

const SystemLog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keywordInput, setKeywordInput] = useState(''); // 입력 중인 키워드 (로컬 state)
  const [refreshMsg, setRefreshMsg] = useState('');
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 20;
  const orderBy = searchParams.get('orderBy') || 'createdAt';
  const orderDirection = searchParams.get('orderDirection') || 'desc';
  const logLevel = searchParams.get('logLevel') || '';
  const keyword = searchParams.get('keyword') || '';
  const fromEventAtParam = searchParams.get('fromEventAt') || '';
  const toEventAtParam = searchParams.get('toEventAt') || '';

  // URL에서 날짜 범위 읽기 (없으면 기본값: 3개월 전부터 오늘까지)
  const getInitialDateRange = () => {
    if (fromEventAtParam && toEventAtParam) {
      const startDate = dayjs(fromEventAtParam, 'YYYY-MM-DD');
      const endDate = dayjs(toEventAtParam, 'YYYY-MM-DD');
      if (startDate.isValid() && endDate.isValid()) {
        return [startDate, endDate];
      }
    }
    // 기본값: 1주일 전부터 오늘까지
    return [dayjs().subtract(1, 'week'), dayjs()];
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);

  // keywordInput을 URL의 keyword와 동기화 (초기값 설정)
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);

  // URL 파라미터가 변경되면 dateRange 업데이트
  useEffect(() => {
    const newDateRange = getInitialDateRange();
    setDateRange(newDateRange);
  }, [fromEventAtParam, toEventAtParam]);

  // 날짜 범위 변경 핸들러
  const handleDateRangeChange = (newDateRange) => {
    if (newDateRange && Array.isArray(newDateRange) && newDateRange.length === 2) {
      setDateRange(newDateRange);
      // URL 파라미터 업데이트
      const current = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...current,
        fromEventAt: dayjs(newDateRange[0]).format('YYYY-MM-DD'),
        toEventAt: dayjs(newDateRange[1]).format('YYYY-MM-DD'),
        page: '1' // 날짜 변경 시 첫 페이지로
      });
    }
  };

  // API 호출용 날짜 형식
  const fromEventAt = useMemo(() => dayjs(dateRange[0]).format('YYYY-MM-DD'), [dateRange]);
  const toEventAt = useMemo(() => dayjs(dateRange[1]).format('YYYY-MM-DD'), [dateRange]);

  // 키워드 입력 핸들러
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };
  
  // 키워드 검색 핸들러 (Enter 키)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      const value = e.target.value.trim();
      if (value.length >= 2) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('keyword', value);
          newParams.set('page', '1'); // 검색 시 첫 페이지로
          return newParams;
        });
      } else if (value.length === 0) {
        // 빈 값이면 keyword 파라미터 제거
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('keyword');
          newParams.set('page', '1');
          return newParams;
        });
      }
    }
  };

  // Query Params Memoization
  const queryParams = useMemo(() => {
    const params = {
      page: currentPage,
      pageSize: pageSize,
      orderBy: orderBy,
      orderDirection: orderDirection
    };
    
    if (logLevel) {
      params.logLevel = logLevel;
    }
    
    if (keyword) {
      params.keyword = keyword;
    }
    
    if (fromEventAt) {
      params.fromEventAt = fromEventAt;
    }
    
    if (toEventAt) {
      params.toEventAt = toEventAt;
    }
    
    return params;
  }, [currentPage, pageSize, orderBy, orderDirection, logLevel, keyword, fromEventAt, toEventAt]);

  const { data: systemLogList, isLoading, error, refetch } = useSystemLogList(queryParams);

  const totalPages = systemLogList?.Total ? Math.ceil(systemLogList.Total / pageSize) : 1;

  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const handleSearch = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
  };

  const handleRefresh = async () => {
    try {
      setRefreshMsg('...');
      await refetch();
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('page', '1');
        return newParams;
      });
      setRefreshMsg('시스템 로그 목록 새로고침 완료!');
    } catch (error) {
      console.error('시스템 로그 목록 새로고침 실패:', error);
      setRefreshMsg('시스템 로그 목록 새로고침 실패!');
    }
    finally {
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    }
  };
  return (
    <div className="system-log-container page-container">
      <div className="system-log-content">
        {/* 필터 섹션 */}
        <div className="flex justify-between align-center mb-10 gap-10">
          <div className="flex w-half gap-10">
            <div className="calendar-filters">
              <Calendar
                mode="range"
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            </div>
            <input 
              type="text" 
              placeholder="키워드 검색 (2자 이상) - 메시지, Payload" 
              className="input-primary flex-1 text-b2-400" 
              value={keywordInput}
              onChange={handleKeywordChange}
              onKeyDown={handleKeywordKeyDown}
            />
          </div>
          <div className="flex justify-end align-center gap-5">
            <p className="text-b2-400 text-primary">{refreshMsg}</p>
            <button className="btn btn-style-5 big-btn flex align-center gap-3" onClick={handleRefresh}>
              <RefreshIcon color='var(--gray-600)'/>
              <p className="text-b2-400">새로고침</p>
            </button>
          </div>
        </div>
        
        {/* 테이블 */}
        <div className="primary-table">
          {
          isLoading ? (
            <SkeletonTable rows={pageSize} columns={7} />
          ) :
          (<table>
            <thead>
              <tr>
                <th width="40px"></th>
                <th width="180px">로그일시</th>
                <th>로그레벨</th>
                <th>메시지</th>
                <th>서비스명</th>
                <th>에러코드</th>
                <th>유저</th>
              </tr>
            </thead>
            <tbody>
              {
                systemLogList?.Items?.map((item, index) => (
                <SystemLogListItem key={item.LogNumber} log={item}/>
              ))
              }
            </tbody>
          </table>
        )}
        </div>

        {/* 페이지네이션 */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          size="medium"
        />

      </div>
    </div>
  );
};

export default SystemLog;